import React from "react";

import { useNavigate } from "@remix-run/react";
import clsx from "clsx";
import { Dot } from "lucide-react";

import Layout from "~/components/layout";
import { useUserProfile } from "~/lib/user";
import { cn } from "~/lib/utils";

import { useIsMobile } from "../../hooks/use-mobile";
import { Button } from "../ui/button";
import { A, H1, H2, LI, P, UL } from "../ui/typography";

interface TableOfContentsItem {
    id: string;
    title: string;
    children?: TableOfContentsItem[];
}

interface LegalPageLayoutProps {
    pageTitle: React.ReactNode; // Allow ReactNode for flexibility (like in Terms)
    lastUpdated: string;
    tableOfContents: TableOfContentsItem[];
    children: React.ReactNode;
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
    pageTitle,
    lastUpdated,
    tableOfContents,
    children,
}) => {
    const isMobile = useIsMobile();
    const renderTableOfContents = (items: TableOfContentsItem[], level = 0) => (
        <UL
            className={cn(
                "space-y-1",
                level === 0 ? "text-sm" : "text-sm mt-1",
                level > 0 ? "pl-4" : "",
            )}
        >
            {items.map((item) => (
                <LI key={item.id} className="relative">
                    <A href={`#${item.id}`} className="block py-1">
                        {item.title}
                    </A>
                    {item.children &&
                        renderTableOfContents(item.children, level + 1)}
                </LI>
            ))}
        </UL>
    );

    const renderMobileTableOfContents = (
        items: TableOfContentsItem[],
        level = 0,
    ) => (
        <ol
            className={cn(
                "space-y-1 mb-4",
                level === 0 ? "list-decimal pl-5" : "list-disc pl-5 mt-1",
            )}
        >
            {items.map((item) => (
                <LI key={item.id}>
                    <A
                        href={`#${item.id}`}
                        className="text-rose-600 hover:underline"
                    >
                        {item.title}
                    </A>
                    {item.children &&
                        renderMobileTableOfContents(item.children, level + 1)}
                </LI>
            ))}
        </ol>
    );
    const navigate = useNavigate();
    const profile = useUserProfile();
    const hasAcceptedTerms = !!profile.data?.terms_accepted_at;

    return (
        <Layout
            fullWidth={true}
            hideSidebar={!hasAcceptedTerms}
            hideBackgroundImage={true} // Assume true for legal pages, can be prop if needed
            title={
                <section className="flex flex-col items-center gap-x-2 md:flex-row">
                    <H1 className="mb-1">{pageTitle}</H1>
                    {!isMobile && <Dot />}
                    <P className="mb-0">Last updated: {lastUpdated}</P>
                    {!hasAcceptedTerms && (
                        <Button
                            onClick={() => navigate(-1)}
                            variant="subtle"
                            size="lg"
                            className="ml-8 hidden font-body md:inline-flex"
                        >
                            Go Back
                        </Button>
                    )}
                </section>
            }
        >
            <div className="mx-auto pb-8">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-10">
                    {/* Sidebar Navigation */}
                    <div className="order-last md:order-first md:col-span-3">
                        {/* Adjusted sticky top to match Terms.tsx */}
                        <div className="sticky top-20 overflow-y-auto rounded-md border-r-2 border-orange-200 bg-white py-8 pl-4 pr-8 shadow-md md:pt-0">
                            <nav className="space-y-4">
                                <H2>On this page</H2>
                                {renderTableOfContents(tableOfContents)}
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div
                        className={clsx(
                            "md:col-span-7 space-y-8 prose",
                            hasAcceptedTerms && "md:col-span-7",
                        )}
                    >
                        {/* Table of Contents - Only visible on mobile */}
                        {isMobile && (
                            <section>
                                <H2 className="mt-0">Table of Contents</H2>
                                {renderMobileTableOfContents(tableOfContents)}
                            </section>
                        )}

                        {/* Page Specific Content */}
                        {children}
                    </div>
                </div>
            </div>
        </Layout>
    );
};
